import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IActionHistory, IActionsState } from '../../types/actionsTypes'
import { fetchProductSystemActions } from '../async-thunks/actions/fetchProductSystemActions'
import { fetchProductCustomActions } from '../async-thunks/actions/fetchProductCustomActions'
import { fetchProductActionsHistory } from '../async-thunks/actions/fetchProductActionsHistory'
import { postAction } from '../async-thunks/actions/postAction'
import _cloneDeep from 'lodash/cloneDeep'
import { getActionFieldErrorKey } from '../../components/ActionsBar/utils/getActionFieldErrorKey'

const initialState: IActionsState = {
    systemActions: {},
    customActions: {},
    actionsParameters: {},
    actionsHistory: {},
    invalidActions: {},
    isLoading: false,
    error: '',
}

const actionsSlice = createSlice({
    name: 'actionsSlice',
    initialState,
    reducers: {
        updateAction: (
            state,
            ac: PayloadAction<{ action: IActionHistory }>
        ) => {
            const action: IActionHistory = ac.payload.action
            const guid = action.product_id
            const actionId = action.action_id
            const actionItems = state.actionsHistory[guid]
            const oneAction = actionItems.find(
                (actionHistoryItem) => actionHistoryItem.action_id === actionId
            )

            state.isLoading = false

            if (oneAction) {
                state.actionsHistory[guid] = actionItems.map((historyItem) =>
                    historyItem.action_id === actionId ? action : historyItem
                )
            } else {
                state.actionsHistory[guid].unshift(action)
            }
        },
        modifyActionParameter: (
            state,
            action: PayloadAction<{ path: string[]; data: any }>
        ) => {
            const { path, data } = action.payload
            const lastIndex = path.length - 1
            const isArray = Array.isArray(data)

            path.reduce((acc: any, cur: string, idx: number) => {
                if (
                    idx === lastIndex &&
                    data &&
                    Object.keys(data).includes(cur)
                ) {
                    acc[cur] = data[cur]
                }
                if (idx === lastIndex && data && isArray) {
                    acc[cur] = data
                }
                if (idx !== lastIndex && acc[cur] === undefined) {
                    acc[cur] = {}
                }
                return acc[cur]
            }, state.actionsParameters)
        },
        resetActionParameters: (state, action: PayloadAction<{ guid: string; actionName: string }>) => {
            const { guid, actionName } = action.payload
            state.actionsParameters[guid]![actionName] = {}

            /* remove action invalid fields */
            state.invalidActions[guid] = state.invalidActions[guid] ?? {}
            state.invalidActions[guid][actionName] = {}
        },
        addCardItemToArrayEnd: (state, action: PayloadAction<{ path: string[]; data: any }>) => {
            const { path, data } = action.payload
            const lastIndex = path.length - 1

            path.reduce((acc: any, cur: string, idx: number) => {
                if (idx === lastIndex && data) {
                    acc[cur] = [...(acc[cur] ?? []), data]
                }
                if (idx !== lastIndex && acc[cur] === undefined) {
                    acc[cur] = {}
                }
                return acc[cur]
            }, state.actionsParameters)
        },
        addCardItemToArrayBeginning: (state, action: PayloadAction<{ path: string[]; data: any }>) => {
            const { path, data } = action.payload
            const lastIndex = path.length - 1

            path.reduce((acc: any, cur: string, idx: number) => {
                if (idx === lastIndex && data) {
                    acc[cur] = [data, ...(acc[cur] ?? [])]
                }
                if (idx !== lastIndex && acc[cur] === undefined) {
                    acc[cur] = {}
                }
                return acc[cur]
            }, state.actionsParameters)
        },
        deleteItemFromArray: (state, action: PayloadAction<{ path: string[]}>) => {
            const { path } = action.payload
            const lastIndex = path.length - 1

            path.reduce((acc: any, cur: string, idx: number) => {
                if (idx === lastIndex) {
                    acc = _cloneDeep(acc.splice(cur, 1))
                }
                if (idx !== lastIndex && acc[cur] === undefined) {
                    acc[cur] = {}
                }
                return acc[cur]
            }, state.actionsParameters)
        },
        setActionInvalid: (state, action: PayloadAction<{ path: string[]}>) => {
            const { path } = action.payload
            const [guid, actionName] = path
            const errorKey = getActionFieldErrorKey(path)
            state.invalidActions[guid] = state.invalidActions[guid] ?? {}
            state.invalidActions[guid][actionName] = state.invalidActions[guid][actionName] ?? {}
            state.invalidActions[guid][actionName][errorKey] = true
        },
        setActionValid: (state, action: PayloadAction<{ path: string[]}>) => {
            const { path } = action.payload
            const [guid, actionName] = path
            const errorKey = getActionFieldErrorKey(path)

            if (state.invalidActions[guid]?.[actionName]?.[errorKey]) {
                delete state.invalidActions[guid]?.[actionName]?.[errorKey]
            }
            state.invalidActions[guid] = state.invalidActions[guid] ?? {}
            state.invalidActions[guid][actionName] = state.invalidActions[guid][actionName] ?? {}
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(
                fetchProductSystemActions.fulfilled,
                (
                    state,
                    action: PayloadAction<{ guid: string; systemActions: any }>
                ) => {
                    state.isLoading = false
                    state.systemActions[action.payload.guid] =
                        action.payload.systemActions
                }
            )
            .addCase(
                fetchProductCustomActions.fulfilled,
                (
                    state,
                    action: PayloadAction<{ guid: string; customActions: any }>
                ) => {
                    state.isLoading = false
                    state.customActions[action.payload.guid] =
                        action.payload.customActions
                }
            )
            .addCase(
                fetchProductActionsHistory.fulfilled,
                (
                    state,
                    action: PayloadAction<{
                        guid: string
                        productActionsHistory: any
                    }>
                ) => {
                    state.isLoading = false
                    state.actionsHistory[action.payload.guid] =
                        action.payload.productActionsHistory
                }
            )
            .addCase(postAction.fulfilled, (state, action) => {
                const { guid, actionResult } = action.payload
                state.isLoading = false
                state.actionsHistory[guid].unshift(actionResult)
            })
    },
})

export const { actions: actionsActions } = actionsSlice
export const { reducer: actionsReducer } = actionsSlice
